import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, Calendar, Bell, CheckCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AutomatedScheduling = () => {
  const navigate = useNavigate();

  const schedulingFeatures = [
    {
      icon: Calendar,
      title: 'Smart Scheduling',
      description: 'Intelligent scheduling algorithms that optimize task distribution based on priority, resources, and compliance requirements.'
    },
    {
      icon: Bell,
      title: 'Automated Reminders',
      description: 'Proactive notifications and alerts ensure critical tasks are completed on time, every time.'
    },
    {
      icon: CheckCircle,
      title: 'Compliance Tracking',
      description: 'Automatic monitoring of scheduled tasks with escalation procedures for missed or delayed activities.'
    },
    {
      icon: Clock,
      title: 'Time Optimization',
      description: 'Advanced algorithms analyze patterns to recommend optimal scheduling for maximum efficiency.'
    }
  ];

  return (
    <div className="min-h-screen">
      <header className="glass-panel-nav">
        <div className="container mx-auto px-4 py-4">
          <Button 
            variant="glass" 
            onClick={() => navigate('/')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
          <h1 className="heading-hero">Automated Scheduling</h1>
          <p className="subheading mt-4">
            Smart scheduling ensures critical tasks are never missed or delayed
          </p>
        </div>
      </header>

      <main className="py-16">
        <div className="container mx-auto px-4">
          {/* Key Features */}
          <section className="mb-16">
            <h2 className="heading-section mb-8 text-center">Scheduling Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {schedulingFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <Card key={feature.title} className="glass-panel border-0 bg-transparent">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-xl bg-crystal-orange/10 border border-crystal-orange/20">
                          <IconComponent className="h-6 w-6 text-crystal-orange" />
                        </div>
                        <CardTitle className="text-white">{feature.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-white/70">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-16">
            <div className="glass-panel-elevated p-8">
              <h2 className="heading-section mb-6 text-center">Scheduling Benefits</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="h-6 w-6 text-crystal-orange mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Never Miss Critical Tasks</h3>
                      <p className="text-white/70">Automated scheduling ensures all food safety tasks are completed on schedule.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="h-6 w-6 text-crystal-orange mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Optimize Resource Allocation</h3>
                      <p className="text-white/70">Smart algorithms distribute workload evenly across teams and locations.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="h-6 w-6 text-crystal-orange mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Reduce Administrative Overhead</h3>
                      <p className="text-white/70">Eliminate manual scheduling and reduce time spent on task management.</p>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="glass-panel p-8">
                    <div className="text-4xl font-bold text-crystal-orange mb-2">99.8%</div>
                    <p className="text-white/70 mb-4">Task completion rate</p>
                    <div className="text-2xl font-bold text-white mb-2">2.5hrs</div>
                    <p className="text-white/70">Average time saved per day</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center">
            <div className="glass-panel-elevated p-8">
              <h3 className="text-2xl font-bold text-white mb-4">
                Streamline Your Scheduling Operations
              </h3>
              <p className="text-white/70 mb-6 max-w-2xl mx-auto">
                Let intelligent automation handle your scheduling while you focus on what matters most - food safety.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="cta-primary px-8 py-3">
                  Start Free Trial
                </Button>
                <Button className="cta-secondary px-8 py-3">
                  View Pricing
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default AutomatedScheduling;